<template>
  <div
    v-if="!isLoading && editForm"
    class="p-5 field-container bg-white rounded-lg overflow-y-auto scrollbar-none gap-y-5"
    style="max-height: calc(80vh - 10.5vh)"
  >
    <div class="section-container">
      <p class="title">
        Receipt Bunker
      </p>
      <div>
        <div
          :class="{
            'upload-file': true,
            'drag-over': isDragOver,
            'disabled': !isEditFuelField
          }"
        >
          <input
            ref="file"
            type="file"
            class="upload-file-input"
            :disabled="isProcessing"
            :accept="acceptedFileTypes.join(',')"
            @change="fileHandler"
          >
          <div
            v-if="!editForm.evidence_attachment_files.value"
            class="dropzone"
            @drop.prevent="fileHandler"
            @dragover.prevent="onDragOver"
            @dragleave.prevent="onDragleave"
          >
            <div class="dropzone-icon">
              <img :src="require('@/assets/shipo-sales-plan-icon-upload.svg')">
            </div>
            <span class="dropzone-text-1">Drag and drop your files here</span>
            <span class="dropzone-text-2">or</span>
            <vs-button
              class="w-32 py-4"
              color="primary"
              @click="$refs.file.click()"
              :disabled="!isEditFuelField || isProcessing"
            >
              Browse
            </vs-button>
          </div>
          <div
            v-else
            class="p-5 w-full flex flex-col items-center justify-center gap-y-3"
            style=""
          >
            <iframe
              v-if="isPDF"
              :src="editForm.evidence_attachment_files.value.url || editForm.evidence_attachment_files.base64"
              style="width: fit-content; max-height: 150px; height: auto; border: none;"
            />
            <img
              v-else-if="!isPDF"
              :src="editForm.evidence_attachment_files.value.thumbnail || editForm.evidence_attachment_files.base64"
              alt="Evidence"
              style="max-width: 100%; max-height: 150px; object-fit: contain"
            >
            <vs-icon
              v-else
              icon="grid_on"
              color="#6546C3"
              size="7.5rem"
            />
            <span class="py-2 w-48 truncate text-center"> {{ editForm.evidence_attachment_files.value.name }} </span>
            <vs-button
              class="w-32 py-4"
              color="primary"
              @click="$refs.file.click()"
              :disabled="!isEditFuelField || isProcessing"
            >
              Changes File
            </vs-button>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="isEditAccountField || (isReadOnly && editForm.fuel_supplier_accounts.value.length > 0)"
      class="section-container"
    >
      <p class="title">
        Account From
      </p>
      <div
        v-if="editForm.fuel_supplier_accounts.value.length > 0"
        class="w-full flex flex-col gap-2"
      >
        <div
          v-for="(fuelSupplier, index) in editForm.fuel_supplier_accounts.value"
          :key="fuelSupplier.id"
          class="w-full flex flex-row justify-between gap-3"
        >
          <div
            class="w-64 flex items-center justify-around"
            :class="{
              'pt-6': (isReadOnly && index === 0),
              'pt-2': (isReadOnly && index !== 0)
            }"
          >
            <span>
              {{ index + 1 }}
            </span>
            <div
              v-if="isEditAccountField"
              class="mx-1 flex items-center justify-center w-8 h-8 icon-transparent-bg"
              @click="editForm.fuel_supplier_accounts.value.length > 1 && handleDeleteFuelSupplier(index)"
              :class="{
                'disabled': editForm.fuel_supplier_accounts.value.length === 1
              }"
            >
              <img
                :src="require('@/assets/shipo-icon-delete.svg')"
                :class="{
                  'inverted': editForm.fuel_supplier_accounts.value.length === 1
                }"
              >
            </div>
          </div>
          <div class="w-3/4 field-container">
            <p
              v-if="isEditAccountField || (isReadOnly && index === 0)"
            >
              Shipper
            </p>
            <div>
              <select-with-search
                v-model="editForm.fuel_supplier_accounts.value[index].fuel_supplier_id"
                :class="{ 'is-not-valid': !editForm.fuel_supplier_accounts.value[index].isValid && !editForm.fuel_supplier_accounts.value[index].fuel_supplier_id }"
                :options="mergedCompanyOptions"
                placeholder="Shipper"
                :disabled="!isEditAccountField || isProcessing"
                style="max-height: 200px"
              />
            </div>
            <span
              v-if="!editForm.fuel_supplier_accounts.value[index].fuel_supplier_id"
              class="text-danger text-sm absolute-bottom-5 left-0"
            >*Required</span>
          </div>
          <div class="w-3/4 field-container">
            <p
              v-if="isEditAccountField || (isReadOnly && index === 0)"
            >
              Quantity
            </p>
            <template>
              <div class="h-auto vs-input">
                <v-autonumeric
                  placeholder="Enter amount"
                  v-model="editForm.fuel_supplier_accounts.value[index].fuel_liters"
                  :class="{ 'is-not-valid': !editForm.fuel_supplier_accounts.value[index].isValid && !editForm.fuel_supplier_accounts.value[index].fuel_liters }"
                  :disabled="!isEditAccountField || isProcessing"
                  class="self-start vs-input--input"
                  :options="globalNumericOptions"
                />
              </div>
              <span
                v-if="!editForm.fuel_supplier_accounts.value[index].fuel_liters"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </template>
          </div>
        </div>
      </div>
      <vs-button
        v-if="isEditAccountField"
        type="filled"
        color="secondary"
        class="self-start font-semibold w-full"
        :disabled="isProcessing"
        @click="addShipper()"
      >
        <div class="flex flex-row items-center justify-center gap-x-2">
          <vs-icon
            icon="add_circle_outline"
            color="#fff"
            size="1.3rem"
          />
          <span>
            Add Shipper
          </span>
        </div>
      </vs-button>
    </div>
    <div class="section-container">
      <p class="title">
        Fuel Supply Base on
      </p>
      <div class="px-3 field-container">
        <span>Trip (Before)</span>
        <div>
          <select-with-search
            v-model="editForm.fuel_consumption_id.value"
            :class="{ 'is-not-valid': !editForm.fuel_consumption_id.isValid && !editForm.fuel_consumption_id.value }"
            :options="housePowerRoutes"
            placeholder="Trip (Before)"
            :disabled="!isEditFuelField || isProcessing"
          />
        </div>
        <span
          v-if="!editForm.fuel_consumption_id.value"
          class="text-danger text-sm absolute-bottom-5 left-0"
        >*Required</span>
      </div>
    </div>
    <div class="section-container">
      <p class="title">
        Last Trip Supplied for Route
      </p>
      <div class="px-3 field-container">
        <span>Last Trip Route</span>
        <div>
          <select-with-multiple
            v-model="editForm.last_trip_route_details.value"
            :options="routeOptions"
            :activate-search="true"
            :is-add-one-by-one="true"
            separator="-"
            placeholder="Last Trip Route"
            :allow-duplicate-values="true"
            :class="{ 'is-not-valid': !editForm.last_trip_route_details.isValid && !editForm.last_trip_route_details.value.length }"
            :disabled="!isEditFuelField || isProcessing"
          />
        </div>
        <span
          v-if="!editForm.last_trip_route_details.value.length"
          class="text-danger text-sm absolute-bottom-5 left-0"
        >*Required</span>
      </div>
    </div>
    <div class="section-container">
      <p class="title">
        Bunker Report
      </p>
      <vs-row
        vs-w="12"
        class="px-3"
      >
        <vs-col
          vs-w="6"
          class="pr-5 mt-5 flex flex-col"
        >
          <span>ROB Before</span>
          <template>
            <div class="vs-input">
              <v-autonumeric
                placeholder="Enter amount"
                v-model="editForm.fuel_liters_rob_before.value"
                :class="{ 'is-not-valid': !editForm.fuel_liters_rob_before.isValid }"
                class="vs-input--input"
                :disabled="!isEditFuelField || isProcessing"
                :options="globalNumericOptions"
              />
            </div>
            <span
              v-if="!editForm.fuel_liters_rob_before.value"
              class="text-danger text-sm absolute-bottom-5 left-0"
            >*Required</span>
          </template>
        </vs-col>
        <vs-col
          vs-w="6"
          class="pr-5 mt-5 flex flex-col"
        />
        <vs-col
          vs-w="6"
          class="pr-5 mt-5 flex flex-col"
        >
          <span>Supplied at SMD</span>
          <template>
            <div class="vs-input">
              <v-autonumeric
                placeholder="Enter amount"
                v-model="editForm.fuel_liters_supplied_at_smd.value"
                :class="{ 'is-not-valid': !editForm.fuel_liters_supplied_at_smd.isValid }"
                class="vs-input--input"
                :disabled="!isEditFuelField || isProcessing"
                :options="globalNumericOptions"
              />
            </div>
            <span
              v-if="!editForm.fuel_liters_supplied_at_smd.value"
              class="text-danger text-sm absolute-bottom-5 left-0"
            >*Required</span>
          </template>
        </vs-col>
        <vs-col
          vs-w="6"
          class="pr-5 mt-5 flex flex-col"
        >
          <div class="px-3 field-container">
            <span>Supplied at BoCT</span>
            <template>
              <div class="vs-input">
                <v-autonumeric
                  placeholder="Enter amount"
                  v-model="editForm.fuel_liters_supplied_at_boct.value"
                  :disabled="!isEditFuelField || isProcessing"
                  class="vs-input--input"
                  :options="globalNumericOptions"
                />
              </div>
            </template>
          </div>
        </vs-col>
        <vs-col
          vs-w="6"
          class="pr-5 mt-5 flex flex-col"
        >
          <span>Total Supplied</span>
          <template>
            <div class="vs-input">
              <v-autonumeric
                v-model="total_supplied"
                class="vs-input--input"
                :disabled="true"
                :options="globalNumericOptions"
              />
            </div>
          </template>
        </vs-col>
        <vs-col
          vs-w="6"
          class="pr-5 mt-5 flex flex-col"
        >
          <span>ROB After</span>
          <template>
            <div class="vs-input">
              <v-autonumeric
                v-model="fuel_liters_rob_after"
                class="vs-input--input"
                :disabled="true"
                :options="globalNumericOptions"
              />
            </div>
          </template>
        </vs-col>
      </vs-row>
    </div>
    <div class="section-container">
      <p class="title">
        Move to Tugboat
      </p>
      <vs-row
        vs-w="12"
        class="px-3"
      >
        <vs-col
          vs-w="6"
          class="pr-5 mt-5 flex flex-col"
        >
          <span>Arrived Bunker Station</span>
          <div class="mt-5 min-w-full flex align-items-center">
            {{ convertToDateTimeFormat(rowData.arrived_dt_bunker) }}
          </div>
        </vs-col>
        <vs-col
          vs-w="6"
          class="mt-5 flex flex-col"
        >
          <span>LCT Alongside</span>
          <date-picker
            v-model="editForm.move_to_tugboat_lct_alongside.value"
            type="datetime"
            value-type="format"
            format="DD MMM YYYY HH:mm"
            class="min-w-full"
            placeholder="dd/mm/yyyy hh:mm"
            :class="{ 'is-not-valid': !editForm.move_to_tugboat_lct_alongside.isValid }"
            :disabled="!isEditFuelField || isProcessing"
          />
          <span
            v-if="!editForm.move_to_tugboat_lct_alongside.value"
            class="text-danger text-sm absolute-bottom-5 left-0"
          >*Required</span>
        </vs-col>
      </vs-row>
      <div class="px-3 field-container">
        <span>Total (Hrs)</span>
        <template>
          <div class="vs-input hour">
            <v-autonumeric
              v-model="move_to_tugboat_total_hours"
              :disabled="true"
              class="vs-input--input"
              :options="globalNumericOptions"
            />
          </div>
        </template>
      </div>
    </div>
    <div class="section-container">
      <p class="title">
        Remarks
      </p>
      <div class="px-3 field-container">
        <span>Remarks</span>
        <vs-textarea
          v-model="editForm.remarks.value"
          placeholder="Remark"
          class="reason-textarea"
          :disabled="!isEditFuelField || isProcessing"
        />
      </div>
    </div>
  </div>
</template>

<script>

import _,{xorBy} from "lodash";
import moment from "moment";

import DatePicker from '@/components/datePicker'
import SelectWithMultiple from "@/components/selectWithMultiple";
import SelectWithSearch from "@/components/selectWithSearch";
import { MAX_FILE_UPLOAD_SIZE_BYTES } from "@/fixtures/Constants";

export default {
  name: 'FuelSupplyForm',
  components: {
    SelectWithMultiple,
    DatePicker,
    SelectWithSearch
  },
  props: {
    accessType: {
      type: String,
      default: 'read'
    },
    rowData: {
      type: Object,
      default: null
    },
    routeOptions: {
      type: Array,
      default: () => []
    },
    companyOptions: {
      type: Array,
      default: () => []
    },
    housePowerRoutes: {
      type: Array,
      default: () => []
    },
    isProcessing: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    rowData: {
      immediate: true,
      handler(val) {
        if (val) {
          this.startEdit(val)
        }
      }
    },
    fuel_liters_rob_after: {
      handler(val) {
        this.editForm.fuel_liters_rob_after.value = val
        if (val > -1 && this.editForm.fuel_liters_rob_before.value > -1) {
          this.total_supplied = val - this.editForm.fuel_liters_rob_before.value
        } else {
          this.total_supplied = 0
        }
      }
    },
    move_to_tugboat_total_hours: {
      handler(val) {
        if (val) {
          this.editForm.move_to_tugboat_total_hours.value = val
        }
      }
    }
  },
  data() {
    return {
      isLoading: true,
      isDragOver: false,
      acceptedFileTypes: [
        'image/*',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ],
      editForm: {
        fuel_consumption_id: { value: null, isValid: true },
        trip_before_route_details: { value: null, isValid: true },
        last_trip_route_details: { value: [], isValid: true },
        fuel_liters_rob_before: { value: 0, isValid: true },
        fuel_liters_supplied_at_smd: { value: 0, isValid: true },
        fuel_liters_supplied_at_boct: { value: 0 },
        fuel_liters_rob_after: { value: 0, isValid: true },
        move_to_tugboat_lct_alongside: { value: null, isValid: true },
        remarks: { value: '' },
        evidence_attachment_files: { value: null },
        fuel_supplier_accounts: { value: [], isValid: true },
        move_to_tugboat_total_hours: { value: 0, isValid: false }
      },
      total_supplied: 0
    }
  },
  computed: {
    isReadOnly() {
      return this.accessType === 'read'
    },
    isEditFuelField() {
      return this.accessType === 'edit_fuel'
    },
    isEditAccountField() {
      return this.accessType === 'edit_account'
    },
    fuel_liters_rob_after() {
      return this.countRobAfter(
        this.editForm?.fuel_liters_rob_before.value,
        this.editForm?.fuel_liters_supplied_at_smd.value,
        this.editForm?.fuel_liters_supplied_at_boct.value
      )
    },
    move_to_tugboat_total_hours() {
      return this.countTotalHoursMove(
        this.editForm?.move_to_tugboat_lct_alongside?.value,
        this.convertToDateTimeFormat(this.rowData?.arrived_dt_bunker)
      ) || null
    },
    isPDF() {
      const fileName = this.editForm.evidence_attachment_files.value?.name
      const ext = fileName.split('.')
      return ext[ext.length - 1] === 'pdf'
    },
    mergedCompanyOptions() {
      if (this.isReadOnly && this.rowData?.fuel_supplier_accounts?.length) {
        const fuelSupplierCompany = this.rowData?.fuel_supplier_accounts.map(( {fuel_supplier: { id, code, name }}) => ({
          id: id,
          value: code,
          text: name
        }))
        return _.unionBy(this.companyOptions, fuelSupplierCompany, 'id' )
      }
      return this.companyOptions
    }
  },
  methods: {
    startEdit(rowData) {
      const formatDate = (date) =>
        date ? this.convertToDateTimeFormat(date) : null

      const mapFormField = (value, isValid = true) => ({
        value: value || null,
        ...(isValid && { isValid })
      })

      const mapRoute = (route) => this.formRoute(route || [])

      const mapFuelSupplier = (suppliers) =>
        suppliers?.map((item) => ({
          ...item,
          fuel_supplier_id: this.mergedCompanyOptions.find(
            (company) => company.id === item.fuel_supplier_id
          )?.value,
          isValid: true
        })) || []

      this.editForm = {
        evidence_attachment_files: mapFormField(rowData?.evidence_attachments, null),
        trip_before_route_details: mapFormField(rowData?.trip_before_route_details ? mapRoute(rowData?.trip_before_route_details) : [], null),
        last_trip_route_details: mapFormField(rowData?.last_trip_route_details ? mapRoute(rowData?.last_trip_route_details) : []),
        fuel_liters_rob_before: mapFormField(rowData?.fuel_liters_rob_before),
        fuel_liters_supplied_at_smd: mapFormField(rowData?.fuel_liters_supplied_at_smd),
        fuel_liters_supplied_at_boct: mapFormField(rowData?.fuel_liters_supplied_at_boct, null),
        fuel_liters_rob_after: mapFormField(
          this.countRobAfter(
            rowData?.fuel_liters_rob_before,
            rowData?.fuel_liters_supplied_at_smd,
            rowData?.fuel_liters_supplied_at_boct
          )
        ),
        move_to_tugboat_lct_alongside: mapFormField(formatDate(rowData?.move_to_tugboat_lct_alongside)),
        remarks: mapFormField(rowData?.remarks, null),
        fuel_supplier_accounts: mapFormField(mapFuelSupplier(rowData?.fuel_supplier_accounts)),
        move_to_tugboat_total_hours: mapFormField(rowData?.move_to_tugboat_total_hours),
        fuel_consumption_id: rowData?.fuel_consumption_id
          ? mapFormField(rowData?.fuel_consumption_id)
          : mapFormField(this.housePowerRoutes.find(({ text }) => text === rowData?.trip_before_route_name)?.value)
      };

      if (this.editForm.fuel_liters_rob_after.value > -1 && this.editForm.fuel_liters_rob_before.value > -1) {
        this.total_supplied = this.editForm.fuel_liters_rob_after.value - this.editForm.fuel_liters_rob_before.value
      } else {
        this.total_supplied = 0
      }
      
      this.isLoading = false
    },
    formRoute(routes) {
      return routes
        .sort((a,b) => a.sequence_order - b.sequence_order)
        .map((route) => {
          const routeOption = this.routeOptions.find(option => option.value.toLowerCase() === route.location_name.toLowerCase())
          if (routeOption) return routeOption
        })
        .filter(Boolean) // removes undefined/ null values
    },
    validateEditForm(form) {
      if (this.isEditAccountField) {
        const suppliers = this.editForm.fuel_supplier_accounts.value;

        form.value.forEach(item => {
          const isValid = !!item?.fuel_supplier_id && !!item?.fuel_liters;
          item.isValid = isValid;

          const supplier = suppliers.find(s => s.id === item.id);
          if (supplier) {
            supplier.isValid = isValid;
          }
        });

        return !form.value.every(item => item.isValid);
      }

      Object.keys(form).forEach(key => {
        if (form[key].hasOwnProperty('isValid')) {
          if (Array.isArray(form[key].value)) {
            form[key].isValid = form[key].value.length > 0
            this.editForm[key].isValid = form[key].value.length > 0
          } else {
            form[key].isValid = !!form[key].value
            this.editForm[key].isValid = !!form[key].value
          }
        }
      })

      return Object.keys(form).some(key =>
        form[key].hasOwnProperty('isValid')
          ? !form[key].isValid
          : false
      )
    },
    fileHandler(e) {
      const inputValue = e.target.files || e.dataTransfer.files || this.$refs.file.files
      const file = inputValue[0]
      if (file) {
        if (!this.validateFileType(file)) {
          this.showFailedNotification(this, 'File may not be other than image, pdf, doc, or xlsx')
          e.target.value = '' // Reset value to trigger onchange listener
          return
        }
        if (file.size > MAX_FILE_UPLOAD_SIZE_BYTES) {
          this.showFailedNotification(this, 'File may not be greater than 5MB')
          e.target.value = '' // Reset value to trigger onchange listener
          return
        }
        const reader = new FileReader()
        reader.onload = (e) => {
          const base64 = e.target.result
          this.editForm.evidence_attachment_files = {
            ...this.editForm.evidence_attachment_files,
            value: file,
            base64: base64
          }
        }
        reader.readAsDataURL(file)
      }
    },
    onDragOver(e) {
      this.isDragOver = true
      e.dataTransfer.dropEffect = 'move'
      e.preventDefault()
    },
    onDragleave() {
      this.isDragOver = false
    },
    validateFileType(file) {
      const fileType = file.type

      return this.acceptedFileTypes.some((type) => {
        if (type.endsWith('/*')) {
          // Handle wildcard, e.g., image/*
          const baseType = type.split('/')[0]
          return fileType.startsWith(`${baseType}/`)
        } else {
          return fileType === type;
        }
      })
    },
    addShipper() {
      const newItem = {
        id: Date.now(),
        fuel_supplier_id: null,
        fuel_liters: null,
        isValid: true
      };
      this.editForm.fuel_supplier_accounts.value.push(newItem);
    },
    handleDeleteFuelSupplier(index) {
      this.editForm.fuel_supplier_accounts.value.splice(index, 1)
    },
    countRobAfter(robBefore, suppliedAtSmd, suppliedAtBoct) {
      robBefore = parseInt(robBefore) || 0
      suppliedAtSmd = parseInt(suppliedAtSmd) || 0
      suppliedAtBoct = parseInt(suppliedAtBoct) || 0
      return robBefore  + suppliedAtSmd + suppliedAtBoct
    },
    countTotalHoursMove(lctAlongside, arrivedAtBunker) {
      lctAlongside = moment.utc(lctAlongside).local()
      arrivedAtBunker = moment.utc(arrivedAtBunker).local()
      if (!lctAlongside.isValid() || !arrivedAtBunker.isValid()) {
        return '0.00';
      }
      // Get difference in minutes, then convert to decimal hours
      const diffInMinutes = lctAlongside.diff(arrivedAtBunker, 'minutes')
      const diffInHours = diffInMinutes / 60
      return diffInHours.toFixed(2)
    },
    getFormData() {
      let form = JSON.parse(JSON.stringify(this.editForm));
      if (this.isEditFuelField) {
        if (!this.editForm.evidence_attachment_files?.value?.hasOwnProperty('url')) {
          form['evidence_attachment_files'].value = this.editForm.evidence_attachment_files.value
        }
        delete form.fuel_supplier_accounts
      } else if (this.isEditAccountField) {
        form = form.fuel_supplier_accounts
      }

      if (this.validateEditForm(form)) {
        this.showFailedNotification(
          this,
          'Some fields is empty, please fill it first',
          'Failed'
        )
        return
      }

      form = Object.entries(form)
        .filter(([_, item]) => item && item?.value !== null)
        .reduce((acc, [key, item]) => {
          acc[key] = item
          return acc
        }, {})

      if (this.isEditFuelField) {
        return this.handleEditFuelForm(form)
      }

      if (this.isEditAccountField) {
        return this.handleEditAccountForm(form)
      }
    },
    handleEditFuelForm(form) {
      const formData = Object.keys(form).reduce((item, key) => {
        const value = form[key].value
        if (key === 'evidence_attachment_files') {
          if (!value.hasOwnProperty('url')) {
            item.append(key, value)
          }
          return item
        } else if (key === 'fuel_consumption_id') {
          const tripBeforeRoute = this.housePowerRoutes.find(route => route.value === value)
          if (tripBeforeRoute) {
            const hpRoute = tripBeforeRoute?.details?.map(({ location_name, sequence_order }) => ({ location_name, sequence_order }))
            item.append('trip_before_route_details', JSON.stringify(hpRoute))
          }
          item.append(key, value)
        } else if (key === 'last_trip_route_details') {
          const routeDetails = value.reduce((arr, route, index) => {
            arr.push({
              location_name: route.value,
              sequence_order: index + 1
            })
            return arr
          }, [])
          item.append(key, JSON.stringify(routeDetails))
        } else if (key !== 'trip_before_route_details') {
          item.append(key, value)
        }
        return item
      }, new FormData())

      formData.append('fill_type', 'detail')
      return formData
    },
    handleEditAccountForm(form) {
      // Check if account form qty is exactly the same as ROB After - ROB Before
      const totalFuelSupply = this.rowData?.fuel_liters_rob_after - this.rowData?.fuel_liters_rob_before
      const totalFuelLiters = form.value.reduce(
        (sum, item) => sum + parseInt(item.fuel_liters, 10),
        0
      );
      if (totalFuelSupply !== totalFuelLiters) {
        this.showFailedNotification(
          this,
          'Total account does not match the total fuel supply.',
          'Failed'
        )
        return
      }

      const data = form.value.map(({ fuel_supplier_id, fuel_liters }) => ({
        fuel_supplier_id: this.mergedCompanyOptions.find(company => company.value === fuel_supplier_id)?.id,
        fuel_liters: parseInt(fuel_liters, 10)
      }))

      const formData = new FormData()
      formData.append('fill_type', 'account')
      formData.append('fuel_supplier_accounts', JSON.stringify(data))

      return formData;
    },
    convertToDateTimeFormat(dateTime, format = 'DD MMM YYYY HH:mm') {
      return dateTime ? moment.utc(dateTime).local().format(format) : '-'
    }
  }
}
</script>

<style lang="scss" scoped>
/deep/.vs-input {
  @apply flex flex-row items-center #{!important};
  @apply w-full h-full;

  .vs-con-input {
    @apply w-full h-full;
  }

  .vs-con-input > input {
    @apply rounded-tr-none rounded-br-none #{!important};
    background: #F1F1FD;

    &:disabled {
      background-color: #ECEBED;
    }
  }

  &.hour {
    &::after {
      content: 'Hrs';
    }
  }

  &::after {
    @apply px-3 rounded-r-lg flex items-center justify-center text-white;
    content: 'Liter';
    background: #6546C3;
    min-height: 100% !important;
  }
}

/deep/.vs-textarea {
  background: #F1F1FD;

  &:disabled {
    background-color: #ECEBED;
    color: black;
  }
}

/deep/.mx-input-wrapper {
  @apply h-12 flex items-center #{!important};
}

/deep/.vs-button-filled {
  &:disabled {
    background-color: #aaaaaa !important;
    color: #6F6F6F;
    opacity: 1;
  }
}

.title {
  @apply text-lg font-semibold text-primary;
}

.section-container {
  @apply w-full flex flex-col gap-y-4;
}

.field-container {
  @apply w-full flex flex-col gap-y-1;
}

.upload-file {
  @apply border;
  @apply border-primary-accent;
  @apply border-dashed;
  @apply leading-3;
  @apply transition-all;
  @apply duration-500;
  --bg-opacity: 0.36;
  border-radius: 1.2rem;
  background-color: rgba(#CDC6E1, var(--bg-opacity));

  &.disabled {
    background-color: #ECEBED;
    border: 1px dashed #818181;
  }

  &.invalid {
    border: 2px dashed #EE443F;
  }

  &.drag-over {
    --bg-opacity: 0.66;
  }

  &-input {
    @apply absolute;
    @apply w-0;
    @apply h-0;
    @apply opacity-0;
  }

  .dropzone {
    @apply flex;
    @apply flex-wrap;
    @apply flex-col;
    @apply justify-center;
    @apply items-center;
    @apply py-8;

    &-text-1 {
      @apply text-primary-accent;
    }

    &-text-2 {
      @apply text-grey-001;
      @apply mt-5;
      @apply mb-4;
    }
  }
}

.is-not-valid {
  /deep/.vs-con-input,
  /deep/.mx-input-wrapper,
  /deep/.bg-accent-multiselect {
    @apply rounded-lg;
    outline: 2px solid #ff647c;
  }
}

.icon-transparent-bg {
  @apply cursor-pointer;
  @apply w-8;
  @apply h-8;
  background-color: #493493;
  border-radius: 0.5rem;

  &.disabled {
    background-color: #DEDEDE;
    pointer-events: none;

    .inverted {
      filter: grayscale(100%);
    }
  }
}
</style>